import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader, removeToken } from '../../utils/auth';
import StatusBadge from '../../components/admin/StatusBadge';
import SubmissionMap from '../../components/admin/SubmissionMap';
import { ArrowLeft, Save, Trash2, MapPin, Calendar, Mail, Phone, FileText } from 'lucide-react';
import '../../styles/admin/AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({ status: false, notes: false });
  const [formData, setFormData] = useState({ status: '', admin_notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/submissions/${id}`, {
        headers: getAuthHeader()
      });
      setSubmission(response.data);
      setFormData({
        status: response.data.status || 'En attente',
        admin_notes: response.data.admin_notes || ''
      });
    } catch (error) {
      if (error.response?.status === 401) {
        removeToken();
        navigate('/admin');
      } else if (error.response?.status === 404) {
        alert('Soumission non trouvée');
        navigate('/admin/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (field) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/submissions/${id}`,
        { [field]: formData[field] },
        { headers: getAuthHeader() }
      );
      await fetchSubmission();
      setEditing({ ...editing, [field === 'admin_notes' ? 'notes' : 'status']: false });
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette soumission ? Cette action est irréversible.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/submissions/${id}`, {
        headers: getAuthHeader()
      });
      alert('Soumission supprimée avec succès');
      navigate('/admin/dashboard');
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  if (!submission) {
    return <div className="admin-error">Soumission non trouvée</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <Link to="/admin/dashboard" className="admin-back-link">
            <ArrowLeft size={20} />
            Retour au dashboard
          </Link>
        </div>
      </div>

      <div className="admin-content">
        <div className="submission-detail">
          {/* Header */}
          <div className="detail-header">
            <div>
              <h2>{submission.prenom} {submission.nom}</h2>
              <p className="detail-date">
                <Calendar size={16} />
                Soumission reçue le {new Date(submission.date).toLocaleDateString('fr-CA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div className="detail-header-actions">
              <StatusBadge status={submission.status} />
              <button onClick={handleDelete} className="admin-delete-btn">
                <Trash2 size={18} />
                Supprimer
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="detail-grid">
            {/* Left Column - Info */}
            <div className="detail-column">
              {/* Contact Info */}
              <div className="detail-card">
                <h3>Informations de contact</h3>
                <div className="detail-info-grid">
                  <div className="detail-info-item">
                    <Mail size={18} />
                    <div>
                      <label>Email</label>
                      <a href={`mailto:${submission.courriel}`}>{submission.courriel}</a>
                    </div>
                  </div>
                  <div className="detail-info-item">
                    <MapPin size={18} />
                    <div>
                      <label>Adresse</label>
                      <p>{submission.adresse}</p>
                    </div>
                  </div>
                  {submission.sourceReference && (
                    <div className="detail-info-item">
                      <FileText size={18} />
                      <div>
                        <label>Source de référence</label>
                        <p>{submission.sourceReference}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Services */}
              <div className="detail-card">
                <h3>Services demandés</h3>
                <div className="services-list">
                  {submission.services.map((service, index) => (
                    <div key={index} className="service-tag">
                      {service}
                    </div>
                  ))}
                </div>
              </div>

              {/* Property Info */}
              <div className="detail-card">
                <h3>Informations du terrain</h3>
                <div className="property-info">
                  <div className="property-area-box">
                    <span className="property-area-label">Superficie</span>
                    <span className="property-area-value">
                      {submission.propertyArea?.toLocaleString() || 'N/A'} pi²
                    </span>
                  </div>
                </div>
              </div>

              {/* Client Notes */}
              {submission.notes && (
                <div className="detail-card">
                  <h3>Notes du client</h3>
                  <p className="client-notes">{submission.notes}</p>
                </div>
              )}

              {/* Photos */}
              {submission.photos && submission.photos.length > 0 && (
                <div className="detail-card">
                  <h3>Photos ({submission.photos.length})</h3>
                  <div className="photos-grid">
                    {submission.photos.map((photo, index) => (
                      <div key={index} className="photo-item">
                        <img
                          src={`${API_URL}/uploads/${photo}`}
                          alt={`Photo ${index + 1}`}
                          onClick={() => window.open(`${API_URL}/uploads/${photo}`, '_blank')}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Admin Actions */}
            <div className="detail-column">
              {/* Status Management */}
              <div className="detail-card">
                <h3>Gestion du statut</h3>
                {editing.status ? (
                  <div className="edit-form">
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="admin-select"
                    >
                      <option value="En attente">En attente</option>
                      <option value="Contacté">Contacté</option>
                      <option value="Soumission envoyée">Soumission envoyée</option>
                      <option value="Complété">Complété</option>
                    </select>
                    <div className="edit-actions">
                      <button
                        onClick={() => handleUpdate('status')}
                        disabled={saving}
                        className="admin-save-btn"
                      >
                        <Save size={16} />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                      <button
                        onClick={() => {
                          setEditing({ ...editing, status: false });
                          setFormData({ ...formData, status: submission.status });
                        }}
                        className="admin-cancel-btn"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="status-display">
                    <StatusBadge status={submission.status} />
                    <button
                      onClick={() => setEditing({ ...editing, status: true })}
                      className="admin-edit-btn"
                    >
                      Modifier
                    </button>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              <div className="detail-card">
                <h3>Notes internes (admin)</h3>
                {editing.notes ? (
                  <div className="edit-form">
                    <textarea
                      value={formData.admin_notes}
                      onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                      placeholder="Ajoutez vos notes internes ici..."
                      rows="6"
                      className="admin-textarea"
                    />
                    <div className="edit-actions">
                      <button
                        onClick={() => handleUpdate('admin_notes')}
                        disabled={saving}
                        className="admin-save-btn"
                      >
                        <Save size={16} />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                      <button
                        onClick={() => {
                          setEditing({ ...editing, notes: false });
                          setFormData({ ...formData, admin_notes: submission.admin_notes });
                        }}
                        className="admin-cancel-btn"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="admin-notes-display">
                      {submission.admin_notes || 'Aucune note interne'}
                    </p>
                    <button
                      onClick={() => setEditing({ ...editing, notes: true })}
                      className="admin-edit-btn"
                    >
                      {submission.admin_notes ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                )}
              </div>

              {/* Map */}
              {submission.propertyCoordinates && submission.propertyCoordinates.length > 0 && (
                <div className="detail-card">
                  <h3>Carte du terrain</h3>
                  <SubmissionMap coordinates={submission.propertyCoordinates} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetail;
